import React, { useState, useEffect, useRef } from 'react';

import { Box, Tab, AppBar } from '@mui/material';
import { TabContext, TabList } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/HomeTwoTone';
import Navbar from '../../components/navbar/navbar';
import FooterContent from '../../components/footer/footerContent';
import loadPageComponent from '../../utils/loadPageComponent';
import useDashboard from './logic/useDashboard';
import './dashboard.css';

const DASHBOARD_PRIMARY = 'var(--sap-sidebar)';

function Dashboard() {
  useDashboard();
  const [selectedTab, setSelectedTab] = useState(0);
  const tabsRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [tabs, setTabs] = useState([
    { id: 'home', title: 'Home', content: <Box className="sap-home">Welcome to the dashboard</Box> },
  ]);

  const handleClick = async (node) => {
    setLoading(true);
    try {
      const PageComponent = await loadPageComponent(node.program_ns);
      const programInstance = node.program_instance || '';
      const isPostMode = /_post$/i.test(programInstance);
      const pageProps = {
        programInstance,
        isReadonly: /_readonly$/i.test(programInstance) || isPostMode,
        isPostMode,
      };
      setTabs((prevTabs) => {
        const existingTab = prevTabs.find((tab) => tab.id === node.id);
        if (existingTab) {
          console.log('🔄 Menu clicked: Force reset for existing tab', node.text);
          const updatedTabs = prevTabs.map((tab) =>
            tab.id === node.id
              ? {
                  ...tab,
                  content: <PageComponent key={`${node.id}-${Date.now()}`} {...pageProps} />,
                  lastResetTime: Date.now()
                }
              : tab
          );
          const existingTabIndex = prevTabs.findIndex((tab) => tab.id === node.id);
          setSelectedTab(existingTabIndex);
          setLoading(false);
          return updatedTabs;
        }
        console.log('➕ Creating new tab for', node.text);
        return [...prevTabs, {
          id: node.id,
          title: node.text,
          content: <PageComponent key={`${node.id}-${Date.now()}`} {...pageProps} />,
          lastResetTime: Date.now()
        }];
      });

      setTabs((currentTabs) => {
        const isNewTab = !currentTabs.slice(0, -1).find((tab) => tab.id === node.id);
        if (isNewTab) {
          setSelectedTab(tabs.length);
        }
        return currentTabs;
      });
    } catch (error) {
      console.error('Error loading page component:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabClose = (index) => {
    setTabs((prevTabs) => {
      const newTabs = prevTabs.filter((tab, i) => i !== index);
      return newTabs;
    });
    setSelectedTab((prevSelected) => (prevSelected === index ? 0 : prevSelected - 1));
  };

  useEffect(() => {
    setSelectedTab(0);
  }, []);

  useEffect(() => {
    const tabsNode = tabsRef.current;
    if (!tabsNode) {
      return;
    }

    const selectedNode = tabsNode.querySelector('[role="tab"][aria-selected="true"]');
    if (selectedNode && typeof selectedNode.scrollIntoView === 'function') {
      selectedNode.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
    }
  }, [selectedTab, tabs.length]);

  return (
    <Box className="sap-shell" sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box className="sap-shell-body" sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        <Navbar onMenuClick={handleClick} setLoading={setLoading} setloadingMessage={'Loading...'} />
        <Box className="sap-workspace" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <TabContext value={selectedTab.toString()}>
            <Box className="sap-module-tabs">
              <TabList
                ref={tabsRef}
                onChange={(event, newValue) => setSelectedTab(parseInt(newValue))}
                aria-label="module tabs"
                textColor="primary"
                indicatorColor="primary"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                visibleScrollbar
                sx={{
                  minHeight: '42px',
                  '& .MuiTabs-scroller': {
                    overflowX: 'auto !important',
                    overflowY: 'hidden !important',
                    scrollbarWidth: 'thin',
                  },
                  '& .MuiTabs-scrollButtons': {
                    width: '28px',
                    color: 'var(--sap-text-muted)',
                    '&.Mui-disabled': {
                      opacity: 0.25,
                    },
                  },
                  '& .MuiTabs-flexContainer': {
                    gap: '4px',
                  },
                }}
              >
                {tabs.map((tab, index) => (
                  <Tab
                    key={tab.id}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {index === 0 && (
                          <HomeIcon sx={{ fontSize: '14px', opacity: 0.7 }} />
                        )}
                        <span>{tab.title}</span>
                        {index > 0 && (
                          <span
                            className="dash-tab-close"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleTabClose(index);
                            }}
                          >
                            <CloseIcon sx={{ fontSize: '11px' }} />
                          </span>
                        )}
                      </Box>
                    }
                    value={index.toString()}
                  />
                ))}
              </TabList>
            </Box>

            {tabs.map((tab, index) => (
              <Box
                className="sap-module-panel"
                key={tab.id}
                role="tabpanel"
                hidden={index.toString() !== selectedTab.toString()}
                sx={{ flexGrow: 1, paddingLeft: '10px', paddingTop: '10px', overflow: 'auto' }}
              >
                {tab.content}
              </Box>
            ))}
          </TabContext>
        </Box>
      </Box>
      <AppBar className="sap-shell-footer" position="static" sx={{ width: '100%', backgroundColor: DASHBOARD_PRIMARY, flexShrink: 0 }}>
        <FooterContent title="Lemo Crane System" loading={loading} />
      </AppBar>
    </Box>
  );
}

export default Dashboard;

